import { useRef, useState } from 'react';

import { ListItemIcon, ListItemText, MenuItem, PopperPlacementType } from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { MenuItem as MenuItemType } from '../Menu.types';
import { Popup } from '../Popup';
import { useAnchorMenuStyles } from './AnchorMenuItem.styles';

export interface AnchorMenuItemProps {
  item: MenuItemType;
  popupPlacement: PopperPlacementType;
  arrow?: string;
  stretched?: boolean;
  index?: number;
  closeParentPopup?: () => void;
}

export const AnchorMenuItem = ({
  index,
  item,
  stretched,
  arrow = 'right',
  popupPlacement = 'bottom',
  closeParentPopup,
}: AnchorMenuItemProps) => {
  const { callback, eventTrigger, icon, subItems, text } = item;
  const [open, setOpen] = useState(false);
  const [preventClose, setPreventClose] = useState(false);
  const anchorRef = useRef(null);

  const classes = useAnchorMenuStyles();

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleEventTrigger = eventType => {
    if (!subItems && eventType === 'click') {
      closePopup();
      typeof callback === 'function' && callback(item);
      return;
    }

    if (eventTrigger !== eventType || (open && preventClose)) {
      return;
    }

    setOpen(prev => !prev || preventClose);
  };

  const handleMenuMouseLeave = () => {
    if (open && preventClose) {
      return;
    }

    setOpen(false);
  };

  const closePopup = () => {
    setPreventClose(false);
    setOpen(false);
    closeParentPopup();
  };

  return (
    <>
      <MenuItem
        data-testid={`anchor-menu-item-${text}`}
        className={`${classes.headerItem.toString()} ${
          stretched ? classes.stretched.toString() : ''
        }`}
        onClick={() => {
          handleEventTrigger('click');
        }}
        onDoubleClick={() => {
          handleEventTrigger('doubleClick');
        }}
        onMouseEnter={() => {
          handleEventTrigger('hover');
        }}
        onMouseLeave={() => {
          setTimeout(() => {
            handleMenuMouseLeave();
          }, 50);
        }}
        ref={anchorRef}
      >
        {icon && <ListItemIcon>{icon}</ListItemIcon>}
        <ListItemText data-testid={`item-${index}`} primary={text} />
        {subItems && (arrow === 'right' ? <ArrowRightIcon /> : <ArrowDropDownIcon />)}
      </MenuItem>
      {subItems && (
        <Popup
          anchorRef={anchorRef}
          closeParentPopup={closePopup}
          handleClose={handleClose}
          items={subItems}
          open={open}
          placement={popupPlacement}
          preventParentClose={preventClose}
          setPreventParentClose={setPreventClose}
        />
      )}
    </>
  );
};
